import { useMemo, useState } from "react";
import { products } from "@/mocks/products";
import { panelModes } from "@/mocks/panelContent";
import { formatBRL } from "@/lib/format";
import { LockedButton, PanelBadge, PanelCard, ReadOnlyNotice } from "../components/PanelUI";

const inputBase =
  "w-full rounded-md border border-background-300 bg-background-100 px-3 py-2 text-sm text-foreground-700 outline-none";

function ReadField({ label, value }: { label: string; value: string }) {
  return (
    <label className="flex flex-col gap-1">
      <span className="text-[11px] uppercase tracking-[0.12em] text-foreground-500">{label}</span>
      <input readOnly value={value} aria-readonly="true" className={`${inputBase} cursor-not-allowed`} />
    </label>
  );
}

export default function CatalogSection() {
  const [query, setQuery] = useState("");
  const [selectedId, setSelectedId] = useState(products[0]?.id ?? "");

  const filtered = useMemo(() => {
    const term = query.trim().toLowerCase();
    if (!term) return products;
    return products.filter((product) =>
      [product.name, product.code, product.category, product.colors.map((color) => color.name).join(" ")]
        .join(" ")
        .toLowerCase()
        .includes(term),
    );
  }, [query]);

  const selected = products.find((product) => product.id === selectedId) ?? filtered[0] ?? products[0];

  return (
    <div className="flex flex-col gap-6">
      <ReadOnlyNotice text={panelModes.viewNotice} />

      <PanelCard
        title="Catálogo"
        description="Busque por nome, código, categoria ou cor. Cada cor tem sua própria imagem, e ao clicar na cor no site a fotografia muda para a cor correspondente."
        action={<PanelBadge tone="primary">{products.length} produtos</PanelBadge>}
      >
        <div className="mb-4 flex items-center gap-2 rounded-md border border-background-300 bg-background-50 px-3 py-2">
          <span className="flex h-5 w-5 items-center justify-center text-foreground-400">
            <i className="ri-search-line text-base" />
          </span>
          <input
            type="search"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Buscar por nome, código, categoria ou cor"
            className="w-full bg-transparent text-sm text-foreground-800 outline-none placeholder:text-foreground-400"
          />
        </div>

        <div className="grid grid-cols-1 gap-5 lg:grid-cols-[340px_1fr]">
          <div className="max-h-[560px] overflow-y-auto rounded-lg border border-background-200">
            {filtered.map((product) => {
              const active = selected?.id === product.id;
              return (
                <button
                  key={product.id}
                  type="button"
                  onClick={() => setSelectedId(product.id)}
                  className={`flex w-full cursor-pointer items-center gap-3 border-b border-background-200 px-3 py-2.5 text-left transition-colors last:border-0 ${
                    active ? "bg-primary-50" : "hover:bg-background-100"
                  }`}
                >
                  <div className="h-14 w-12 shrink-0 overflow-hidden rounded-md bg-background-100">
                    <img
                      src={product.colors[0].image}
                      alt={`${product.name} — ${product.category}`}
                      title={`${product.name} — Amora Belle`}
                      className="h-full w-full object-cover object-top"
                    />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm text-foreground-900">{product.name}</p>
                    <p className="text-[11px] text-foreground-500">
                      {product.code} · {product.category}
                    </p>
                  </div>
                  <span className="whitespace-nowrap text-xs text-foreground-700">
                    {formatBRL(product.promoPrice ?? product.price)}
                  </span>
                </button>
              );
            })}
            {filtered.length === 0 ? (
              <p className="px-3 py-6 text-center text-sm text-foreground-500">
                Nenhum produto encontrado para esta busca.
              </p>
            ) : null}
          </div>

          {selected ? (
            <div className="rounded-lg border border-background-200 bg-background-50 p-4 md:p-5">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="flex flex-wrap items-center gap-2">
                  <h3 className="font-heading text-xl text-foreground-900">{selected.name}</h3>
                  <PanelBadge tone="primary">{selected.code}</PanelBadge>
                  <PanelBadge tone="neutral">{selected.category}</PanelBadge>
                  {selected.tag ? <PanelBadge tone="accent">{selected.tag}</PanelBadge> : null}
                </div>
                <div className="flex flex-wrap gap-2">
                  <LockedButton icon="ri-edit-2-line">Editar</LockedButton>
                  <LockedButton icon="ri-file-copy-line" hint="Duplicar produto">
                    Duplicar
                  </LockedButton>
                  <LockedButton icon="ri-inbox-archive-line" hint="Arquivar produto">
                    Arquivar
                  </LockedButton>
                </div>
              </div>

              <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
                <ReadField label="Nome" value={selected.name} />
                <ReadField label="Código" value={selected.code} />
                <ReadField label="Categoria" value={selected.category} />
                <ReadField label="Preço" value={formatBRL(selected.price)} />
                <ReadField
                  label="Preço anterior"
                  value={selected.promoPrice ? formatBRL(selected.price) : "—"}
                />
                <ReadField label="Parcelamento" value={`${selected.installments}x sem juros`} />
                <ReadField label="Selo" value={selected.tag ?? "—"} />
                <ReadField label="Estoque" value={`${selected.stock} peças`} />
                <ReadField label="Tecido" value={selected.fabric} />
                <ReadField label="Modelagem" value={selected.fit} />
                <ReadField label="Cuidados" value={selected.care} />
                <ReadField label="Tamanhos" value={selected.sizes.join(", ")} />
              </div>

              <div className="mt-4">
                <ReadField label="Descrição" value={selected.description} />
              </div>

              <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <p className="mb-2 text-[11px] uppercase tracking-[0.12em] text-foreground-500">
                    Cores, código hexadecimal e imagem
                  </p>
                  <div className="flex flex-wrap gap-3">
                    {selected.colors.map((color) => (
                      <div key={color.name} className="flex items-center gap-2 rounded-md border border-background-200 bg-background-100 px-2.5 py-2">
                        <span
                          className="h-6 w-6 shrink-0 rounded-full border border-background-300"
                          style={{ backgroundColor: color.hex }}
                        />
                        <span className="text-xs text-foreground-700">
                          {color.name} · {color.hex}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="flex flex-wrap gap-3">
                  {selected.colors.map((color) => (
                    <div key={color.name} className="w-[84px]">
                      <div className="h-[104px] w-full overflow-hidden rounded-md border border-background-200 bg-background-100">
                        <img
                          src={color.image}
                          alt={`${selected.name} na cor ${color.name}`}
                          title={`${selected.name} — ${color.name}`}
                          className="h-full w-full object-cover object-top"
                        />
                      </div>
                      <p className="mt-1 truncate text-[10px] text-foreground-500">{color.name}</p>
                    </div>
                  ))}
                </div>
              </div>

              <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-4">
                {[
                  { label: "Destaque", value: selected.tag === "Destaque" },
                  { label: "Novidade", value: selected.isNew },
                  { label: "Mais vendido", value: selected.tag === "Mais vendido" },
                  { label: "Ativo", value: true },
                ].map((flag) => (
                  <div key={flag.label} className="flex items-center gap-2 rounded-md border border-background-200 bg-background-100 px-3 py-2">
                    <span className={`flex h-4 w-4 items-center justify-center rounded-sm border ${flag.value ? "border-primary-500 bg-primary-500" : "border-background-400 bg-background-50"}`}>
                      {flag.value ? <i className="ri-check-line text-[10px] text-background-50" /> : null}
                    </span>
                    <span className="text-xs text-foreground-700">{flag.label}</span>
                  </div>
                ))}
              </div>

              <p className="mt-4 text-[11px] leading-relaxed text-foreground-500">
                Produtos relacionados, SEO e status ativo/arquivado também são editáveis pela
                administração Vellora. Aqui eles aparecem apenas para consulta.
              </p>
            </div>
          ) : null}
        </div>
      </PanelCard>
    </div>
  );
}